import { useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { ThreeDotsVertical } from "react-bootstrap-icons";
import { ReactComponent as CommentIcon } from "assets/img/icons/comment.svg";
import { ReactComponent as DislikeIcon } from "assets/img/icons/dislike.svg";
import { ReactComponent as LikeIcon } from "assets/img/icons/like.svg";
import { Table as T, Tooltip } from "components/elements";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { trpc } from "lib/trpc";
import { VendorListing } from "@compass/shared/types";
import { VendorDetailedEntry } from "./VendorDetailedEntry";

type VendorTableEntryProps = {
    vendor: VendorListing;
    handleEdit: (data: VendorListing) => void;
    setDeletePopup: React.Dispatch<
        React.SetStateAction<{
            id: number | null;
            isOpen: boolean;
        }>
    >;
};

const CATEGORY_BG: Record<string, string> = {
    accounting: "bg-[#FFE9CA]",
    banking: "bg-[#CEF7D9]",
    consulting: "bg-[#F0E7FC]",
    insurance: "bg-[#FFE9CA]",
    legal: "bg-[#CEF7D9]",
    marketing: "bg-[#F0E7FC]",
    technology: "bg-[#FFE9CA]",
    other: "bg-[#CEF7D9]",
};

const normalizeCategory = (v: unknown) => (typeof v === "string" ? v.trim().toLowerCase() : "");

export const VendorTableEntry = ({ vendor, setDeletePopup, handleEdit }: VendorTableEntryProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const navigate = useNavigate();
    const { mutateAsync } = trpc.vendors.vendorVotes.create.useMutation();
    const { mutateAsync: mutateDeleteAsync } = trpc.vendors.vendorVotes.delete.useMutation();
    const queryClient = trpc.useContext();

    const menuRef = useRef<HTMLDivElement | null>(null);

    const openModal = () => {
        setIsOpen(true);
        navigate({ to: "/vendors/$id", params: { id: String(vendor.id) } });
    };

    const closeModal = () => {
        setIsOpen(false);
        navigate({ to: "/vendors" });
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setDeleteModal(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleVendorVote = async (vote: any) => {
        const params = {
            id: vendor.userVoteData?.id || null,
            vendorId: vendor.id,
            vote: vote,
        };

        const findSameVote = vendor.userVoteData?.vote === vote;

        if (findSameVote && vendor.userVoteData?.id) {
            mutateDeleteAsync(vendor.userVoteData.id, {
                onSuccess: () => {
                    queryClient.vendors.latest.invalidate();
                },
                onError: () => {
                    console.error("Error");
                    enqueueSnackbar({ variant: "error" });
                },
            });
            return;
        }

        mutateAsync(params, {
            onSuccess: () => {
                queryClient.vendors.latest.invalidate();
            },
            onError: (e) => {
                console.error(e);
                enqueueSnackbar(`${e}`, { variant: "error" });
            },
        });
    };
    return (
        <>
            <T.Row className="even:bg-brand-polarice cursor-pointer odd:bg-[#edf2f7]">
                <T.Cell>
                    <button
                        className="hover:gray-500 m-0 bg-transparent p-0 text-left hover:underline"
                        onClick={openModal}
                    >
                        {vendor.name}
                    </button>
                </T.Cell>
                <T.Cell className="w-20">
                    {vendor.contactEmail ? (
                        <>
                            {vendor.contactName} <br />
                            <a href={`mailto:${vendor.contactEmail}`}>{vendor.contactEmail}</a>
                        </>
                    ) : (
                        ""
                    )}
                    {!vendor.contactEmail ? vendor.contactName : ""}
                </T.Cell>
                <T.Cell>
                    {(() => {
                        const key = normalizeCategory(vendor.category);
                        if (!key) return null; // null/undefined/empty -> render nothing
                        const bg = CATEGORY_BG[key] ?? "bg-[#FFE9CA]"; // unknown but non-empty -> fallback
                        return (
                            <div className={`rounded-full px-4 py-2 text-center ${bg}`}>
                                {vendor.category}
                            </div>
                        );
                    })()}
                </T.Cell>
                <T.Cell>
                    {vendor.website ? (
                        <Tooltip content={vendor.website}>
                            <a
                                href={vendor.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 hover:underline"
                            >
                                Go to website
                            </a>
                        </Tooltip>
                    ) : (
                        ""
                    )}
                </T.Cell>
                <T.Cell>
                    <div className="flex items-center gap-1">
                        <DislikeIcon
                            color={vendor.userVoteData?.vote === "LIKE" ? "blue" : "black"}
                            onClick={() => handleVendorVote("LIKE")}
                            className="h-[16px] w-[16px] cursor-pointer"
                        />
                        <div className="w-4 text-center">{vendor.likesVotesCount ?? 0}</div>

                        <LikeIcon
                            color={vendor.userVoteData?.vote === "UNLIKE" ? "blue" : "black"}
                            onClick={() => handleVendorVote("UNLIKE")}
                            className="h-[16px] w-[16px] cursor-pointer"
                        />
                        <div className="w-4 text-center">{vendor.unlikesVotesCount ?? 0}</div>
                        <div
                            onClick={() => (isOpen ? closeModal() : openModal())}
                            className="flex items-center gap-1"
                        >
                            <CommentIcon className="h-[16px] w-[16px] cursor-pointer" />
                            <div className=" w-4 text-center">{vendor.commentCount ?? 0}</div>
                        </div>
                    </div>
                </T.Cell>
                <T.Cell>
                    <FavoriteButton target={{ type: "vendor", id: vendor.id }} size={16} />
                </T.Cell>
                <T.Cell>
                    {vendor.userCanEdit && (
                        <div className="relative inline-block text-left">
                            <ThreeDotsVertical
                                onClick={() => setDeleteModal(true)}
                                className="cursor-pointer"
                            />
                            {deleteModal && (
                                <div
                                    ref={menuRef}
                                    className="absolute right-0 z-50 mt-2 w-40 rounded-md border border-gray-200 bg-white shadow-lg"
                                    role="menu"
                                    aria-orientation="vertical"
                                    aria-labelledby="menu-button"
                                >
                                    <div className="py-1">
                                        {
                                            <button
                                                className="w-full px-4 py-2 text-left  text-sm text-gray-700 hover:bg-gray-100"
                                                onClick={() => {
                                                    handleEdit(vendor);
                                                    setDeleteModal(false);
                                                }}
                                            >
                                                Edit
                                            </button>
                                        }
                                        <button
                                            className="w-full px-4 py-2 text-left  text-sm text-gray-700 hover:bg-gray-100"
                                            onClick={() => {
                                                setDeletePopup({ id: vendor.id, isOpen: true });
                                                setDeleteModal(false);
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </T.Cell>
            </T.Row>

            <VendorDetailedEntry
                vendor={vendor}
                isOpen={isOpen}
                setIsOpen={(open) => (open ? openModal() : closeModal())}
            />
        </>
    );
};
